import { CustomizationComponentProps } from '@lumapps/customizations/types';
import { ListItemProps, UserBlockSize, Theme } from '@lumapps/lumx/react';
import { Route } from '@lumapps/router';
import { User, UserProfile } from '@lumapps/user/types';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

export enum USER_MENU_LINK_TYPE {
    LINK = 'LINK',
    BUTTON = 'BUTTON',
    DIVIDER = 'DIVIDER',
    CUSTOMIZATION = 'CUSTOMIZATION',
}

export type UserMenuLink = {
    key: string;
    icon?: string;
    label?: string;
    url?: string;
    target?: string;
    type?: USER_MENU_LINK_TYPE;
    onClick?: (evt?: any) => void;
    route?: Route;
    isLoading?: boolean;
    tooltipLabel?: string;
    isDisabled?: boolean;
    customizationProps?: CustomizationComponentProps;
};

export type UserProfileListItemProps = ListItemProps & {
    /** The avatar of the user */
    avatarImageSrc?: string;
    /** The name */
    name?: string;
    /** The url to the userProfile */
    userProfileUrl?: string;
    /** Custom className */
    className?: string;
    /** The list item size */
    size?: ListItemProps['size'];
    /** The block size */
    userBlockSize?: UserBlockSize;
    /** The theme */
    theme?: Theme;
    /** Custom? style */
    style?: Record<string, string>;
    /** callback when the view user profile link is clicked */
    onClick?: () => void;
    /** scope where this component is used. This prop will be used for tracking purposes */
    scope: string;
};

/**
 * enum with all the available views for user profile
 */
export enum USER_PROFILE_VIEW {
    activity = 'activity',
    about = 'about',
}

export interface UserProfileNavigationItemsType {
    /** The label that will be shown in the navigation bar. */
    label: string;
    /** The view name to which the user will be redirected */
    view: USER_PROFILE_VIEW;
    /**
     * Conditions to fulfill to display the item.
     * All given conditions must be fulfilled for the item to be displayed
     */
    displayedWhen?: {
        hasSocial?: boolean;
    };
}

export interface GetUserProfileParams {
    /** whether we want to retrieve this information from the memory cache */
    getCacheFirst?: boolean;
    /** user's id */
    userId: string;
    /** Whether the cached value should be cleared before fetching. */
    clearCacheFirst?: boolean;
    /** Whether we want to retrieve the empty fields */
    includeEmptyFields?: boolean;
}

export interface GetOrgChartParams {
    /** whether we want to retrieve this information from the memory cache */
    getCacheFirst?: boolean;
    /** user's id */
    userId: string;
    /** Whether the cached value should be cleared before fetching. */
    clearCacheFirst?: boolean;
}

export interface OrganizationChartUserType
    extends Pick<
        UserProfile,
        | 'id'
        | 'profilePictureUrl'
        | 'email'
        | 'firstName'
        | 'lastName'
        | 'jobTitle'
        | 'departmentName'
        | 'locationName'
        | 'fullName'
    > {
    reports?: {
        values: Array<OrganizationChartUserType>;
        more: boolean;
        cursor: string | null;
    };
}

export interface UserProfileRoutingData {
    user?: Partial<User>;
}

export interface UserProfilePermissions {
    isCommunityAdmin: boolean;
}

/** Enum for user profile specific statuses */
export enum UserProfileSpecificStatus {
    /**
     * Status set when saving the user profile, as we only need to reload the user fields
     * and not the whole profile.
     */
    reloadingUserFields = 'reloadingUserFields',
}

export type UserProfileStatuses = BaseLoadingStatus | UserProfileSpecificStatus;
export const UserProfileStatuses = { ...BaseLoadingStatus, ...UserProfileSpecificStatus };
